// ─────────────────────────────────────────────────────────────
// CloudflareIndexManager — manages projects/index.json in R2
// The index is a fast catalog: { uuid: { name, modified } }
// It is never the source of truth — projects are always safe
// in their individual R2 folders even if the index is damaged.
// ─────────────────────────────────────────────────────────────

import {UUID} from "@opendaw/lib-std"
import {ProjectStorage} from "@opendaw/studio-core"
import {CloudflareHandler} from "./CloudflareHandler"

const INDEX_PATH = "projects/index.json"

type ProjectIndex = Record<string, {name: string; modified: string}>

export namespace CloudflareIndexManager {

    export const readProjectIndex = async (handler: CloudflareHandler): Promise<ProjectIndex> => {
        try {
            const data = await handler.download(INDEX_PATH)
            return JSON.parse(new TextDecoder().decode(data)) as ProjectIndex
        } catch {
            return {}
        }
    }

    export const writeProjectIndex = async (
        handler: CloudflareHandler,
        index: ProjectIndex
    ): Promise<void> => {
        const data = new TextEncoder().encode(JSON.stringify(index, null, 2))
        await handler.upload(INDEX_PATH, data.buffer as ArrayBuffer)
    }

    export const updateProjectInIndex = async (
        handler: CloudflareHandler,
        projectUuid: Uint8Array
    ): Promise<void> => {
        const uuidStr = UUID.toString(projectUuid)

        // Load the current meta from OPFS to get the project name
        const metaBuffer = await ProjectStorage.loadMeta(projectUuid)
        const meta = JSON.parse(new TextDecoder().decode(metaBuffer))

        const index = await readProjectIndex(handler)
        index[uuidStr] = {
            name: meta.name ?? "Untitled",
            modified: meta.modified ?? new Date().toISOString()
        }

        await writeProjectIndex(handler, index)
    }

    export const removeProjectFromIndex = async (
        handler: CloudflareHandler,
        uuidStr: string
    ): Promise<void> => {
        const index = await readProjectIndex(handler)
        delete index[uuidStr]
        await writeProjectIndex(handler, index)
    }
}
